import React, { useState, useEffect } from 'react';
import { X, Upload, Presentation, RefreshCcw, Download, CheckCircle, AlertTriangle, Sparkles, Edit } from 'lucide-react';
import './PPTGenerationModal.css';
import '../../rfp/shared/components/ExportModal.css';
import CustomLoader from '../../../shared/components/ui/CustomLoader';
import api from '../../../services/api';
import { useRealTimeChat } from '../../chat/context/RealTimeChatContext';
import { useThemes } from '../../themes/hooks/useThemes';

const PPTGenerationModal = ({ isOpen, onClose, rfpId, onGenerateSuccess, navigate, editableData }) => {
    const [loading, setLoading] = useState(true);
    const [isRegenerating, setIsRegenerating] = useState(false);
    const [isDownloading, setIsDownloading] = useState(false);
    const [pptData, setPptData] = useState(null);
    const [selectedThemeId, setSelectedThemeId] = useState('');
    const [error, setError] = useState('');
    const [selectedLang, setSelectedLang] = useState('EN');
    const { subscribeToMessages } = useRealTimeChat();
    const { themes, isLoading: loadingThemes } = useThemes();
    const [progressMessage, setProgressMessage] = useState('Initiating slide generation...');
    const [progressPercent, setProgressPercent] = useState(0);

    // Extract RFP status & Proposal decision
    const rfpStatus = editableData?.global_status?.label || editableData?.proposal_stage || 'Unknown';
    const currentStage = String(editableData?.global_status?.label || editableData?.proposal_stage || '').toLowerCase();
    const isAccepted = ['client accepted', 'proposal accepted', 'accepted', '6'].some(s => currentStage.includes(s));
    const isRejected = ['client rejected', 'proposal rejected', 'rejected', '7'].some(s => currentStage.includes(s));
    let proposalDecision = 'Undecided';
    if (isAccepted) proposalDecision = 'Proposal Accepted';
    else if (isRejected) proposalDecision = 'Proposal Rejected';

    const checkPPTStatus = async () => {
        setLoading(true);
        setError('');
        try {
            const response = await api.get(`/ppt-config/rfp/${rfpId}/all`);
            setPptData(response.data);
        } catch (err) {
            console.error('Error checking PPT status:', err);
            setError('Failed to fetch presentation status.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
            checkPPTStatus();
        } else {
            document.body.style.overflow = 'unset';
        }
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isOpen, rfpId]);

    // Set default theme when themes load
    useEffect(() => {
        if (themes.length > 0 && !selectedThemeId) {
            const backendThemeId = editableData?.template_id || editableData?.rfp_details?.template_id || editableData?.metadata?.template_id || pptData?.template_id || pptData?.metadata?.template_id;

            if (backendThemeId && themes.some(t => String(t.id) === String(backendThemeId))) {
                setSelectedThemeId(backendThemeId);
            } else {
                setSelectedThemeId(themes[0].id);
            }
        }
    }, [themes, selectedThemeId, editableData, pptData]);

    const handleGenerate = async (isRegenerate = false) => {
        setIsRegenerating(true);
        setProgressMessage('Initiating slide generation...');
        setProgressPercent(0);
        setError('');
        try {
            const formData = new FormData();
            formData.append('rfp_id', rfpId);
            if (selectedThemeId) {
                formData.append('template_id', selectedThemeId);
            }

            const response = await api.post(`ppt-config/rfp/${rfpId}/generate-slides`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });

            if (response.status === 200 || response.status === 201) {
                const isSyncResponse = response.data && (response.data.ppt_generated || response.data.slide_order || response.data.slides);
                if (isSyncResponse) {
                    navigate(`/proposals/ppt-customization/${rfpId}`, {
                        state: {
                            generationResponse: response.data
                        }
                    });
                    onClose();
                    setIsRegenerating(false);
                } else {
                    setProgressMessage('Generation started, preparing presentation slides...');
                }
            } else {
                setIsRegenerating(false);
            }
        } catch (err) {
            console.error('Generation error:', err);
            setError(err.response?.data?.detail || 'Failed to generate presentation.');
            setIsRegenerating(false);
        }
    };

    useEffect(() => {
        if (!isRegenerating) return;

        const handleMessage = (payload) => {
            const type = payload.type || payload.event;
            const msgPayload = payload.payload || payload.data || payload;
            const eventRfpId = msgPayload?.rfpId || msgPayload?.rfp_id;

            // Verify the message belongs to this specific RFP
            if (String(eventRfpId) === String(rfpId)) {
                if (type === 'PPT_GENERATION_PROGRESS') {
                    const stepText = msgPayload?.step ? `[${msgPayload.step}] ` : '';
                    setProgressMessage(`${stepText}${msgPayload?.message || 'Generating presentation slides...'}`);
                    if (msgPayload?.progress !== undefined) {
                        setProgressPercent(Number(msgPayload.progress));
                    }
                } else if (type === 'PPT_GENERATION_COMPLETED') {
                    setIsRegenerating(false);
                    navigate(`/proposals/ppt-customization/${rfpId}`, {
                        state: { generationResponse: msgPayload?.data || msgPayload }
                    });
                    onClose();
                } else if (type === 'PPT_GENERATION_FAILED') {
                    setIsRegenerating(false);
                    setError(msgPayload?.message || 'Failed to generate presentation slides.');
                }
            }
        };

        const unsubscribe = subscribeToMessages(handleMessage);
        return () => unsubscribe();
    }, [isRegenerating, rfpId, navigate, onClose, subscribeToMessages]);

    const handleDownloadRedirect = () => {
        navigate(`/proposals/ppt-customization/${rfpId}`);
        onClose();
    };

    const isPptGenerated = pptData?.ppt_generated === 1 || pptData?.slide_order?.ppt_generated === 1;

    const handleDirectDownloadPPT = async () => {
        setIsDownloading(true);
        setError('');
        try {
            const lang = selectedLang ? selectedLang.toLowerCase() : 'en';
            let headers = {
                'rfp_id': rfpId,
                'Accept-Language': lang,
                'language': lang
            };
            
            let baseUrl = `/rfp/report/${rfpId}?format=pptx&language=${lang}`;
            if (selectedThemeId) {
                baseUrl += `&template_id=${selectedThemeId}`;
            }

            const response = await api.get(baseUrl, { headers, responseType: 'blob' });

            if (response.status !== 200) throw new Error('Failed to download PPT.');
            const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.presentationml.presentation' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `RFP_Presentation_${rfpId}.pptx`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Download failed:', err);
            setError(err.message || 'Failed to download PPTX.');
        } finally {
            setIsDownloading(false);
        }
    };

    const handleViewPPT = () => {
        navigate(`/proposals/ppt-customization/${rfpId}`);
        onClose();
    };

    if (!isOpen) return null;

    const isAnyActionRunning = isRegenerating || isDownloading;

    return (
        <div className="ppt-modal-overlay">
            <div className="ppt-modal-content" style={{ padding: '10px' }}>
                <div className="ppt-modal-header">
                    <div className="ppt-title-section">
                        <Presentation size={32} className="ppt-icon-main" />
                        <div>
                            <h3>Presentation Configuration</h3>
                            <p>Configure your presentation slides</p>
                        </div>
                    </div>
                    <button
                        className="ppt-close-btn"
                        onClick={onClose}
                        disabled={isAnyActionRunning}
                        style={isAnyActionRunning ? { pointerEvents: 'none', cursor: 'not-allowed', opacity: 0.3 } : {}}
                    >
                        <X size={20} />
                    </button>
                </div>

                <div className="ppt-modal-body">
                    {loading ? (
                        <div className="ppt-loading">
                            <CustomLoader size={32} />
                            <p>Checking presentation status...</p>
                        </div>
                    ) : isRegenerating ? (
                        <div className="ppt-loading" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '2rem 0', gap: '1rem' }}>
                            <CustomLoader size={32} />
                            <p style={{ fontWeight: '500', fontSize: '15px', color: '#1E293B', margin: 0 }}>Generating slides...</p>

                            {/* Progress bar container */}
                            <div style={{ width: '80%', background: '#F1F5F9', borderRadius: '8px', height: '8px', overflow: 'hidden', border: '1px solid #E2E8F0', marginTop: '5px', marginBottom: '5px' }}>
                                <div style={{ width: `${progressPercent}%`, background: 'linear-gradient(90deg, #FF5526 0%, #FF5526 100%)', height: '100%', transition: 'width 0.4s ease-out' }} />
                            </div>
                            <span style={{ fontSize: '12px', fontWeight: '600', color: '#FF5526' }}>{progressPercent}% Completed</span>

                            <p style={{ fontSize: '13px', color: '#64748B', margin: 0, textAlign: 'center', maxWidth: '80%', lineHeight: '1.5' }}>
                                {progressMessage}
                            </p>
                        </div>
                    ) : (
                        <>
                            {error && (
                                <div className="ppt-error-message">
                                    <AlertTriangle size={18} />
                                    <span>{error}</span>
                                </div>
                            )}

                            {/* Theme Details and Language Section */}
                            <div className="export-section" style={{ border: 'none', padding: 0, background: 'transparent', marginBottom: '1.25rem' }}>
                                <div className="export-section-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                                    <h3 className="export-section-title" style={{ margin: 0 }}>Theme Details </h3>
                                    <button className="export-edit-theme-btn" onClick={() => navigate('/themes')} title="Edit Theme">
                                        Edit<Edit size={16} />
                                    </button>
                                </div>

                                <div className="export-theme-container">
                                    {loadingThemes ? (
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#64748B', fontSize: '0.8rem' }}>
                                            <CustomLoader size={16} /> Loading themes...
                                        </div>
                                    ) : (
                                        <>
                                            <div style={{ display: 'flex', flexDirection: 'row', gap: '1.5rem', marginBottom: '1rem', alignItems: 'flex-start' }}>
                                                {/* Theme Selection Dropdown */}
                                                <div className="export-form-group" style={{ flex: 1, margin: 0 }}>
                                                    <label style={{ display: 'block', marginBottom: '8px', fontSize: '0.85rem', fontWeight: 600, color: '#1E293B', textAlign: 'left' }}>Change Theme</label>
                                                    <div className="export-input-wrapper">
                                                        <select 
                                                            value={selectedThemeId} 
                                                            onChange={(e) => setSelectedThemeId(e.target.value)}
                                                            className="export-select"
                                                            disabled={isAnyActionRunning}
                                                            style={{ paddingLeft: '14px', opacity: isAnyActionRunning ? 0.5 : 1, cursor: isAnyActionRunning ? 'not-allowed' : 'pointer' }}
                                                        >
                                                            {themes.length === 0 && <option value="">No themes available</option>}
                                                            {themes.map(t => (
                                                                <option key={t.id} value={t.id}>{t.name}</option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                </div>

                                                {/* Language Selection Dropdown */}
                                                <div className="ppt-config-section" style={{ flex: 1, margin: 0 }}>
                                                    <label className="section-label" style={{ display: 'block', marginBottom: '8px', fontSize: '0.85rem', fontWeight: 600, color: '#1E293B', textAlign: 'left' }}>Language</label>
                                                    <select
                                                        value={selectedLang}
                                                        onChange={(e) => setSelectedLang(e.target.value)}
                                                        disabled={isAnyActionRunning}
                                                        style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid #CBD5E1', background: '#F8F9FA', fontSize: '0.85rem', outline: 'none', color: '#1E293B', opacity: isAnyActionRunning ? 0.5 : 1, cursor: isAnyActionRunning ? 'not-allowed' : 'pointer' }}
                                                    >
                                                        <option value="EN">English</option>
                                                        <option value="AR">Arabic</option>
                                                        <option value="ES">Spanish</option>
                                                        <option value="PT">Portuguese</option>
                                                    </select>
                                                </div>
                                            </div>

                                            {/* Theme Preview Grid */}
                                            {selectedThemeId && themes.find(t => t.id === selectedThemeId) && (
                                                (() => {
                                                    const theme = themes.find(t => t.id === selectedThemeId);
                                                    return (
                                                        <div className="export-theme-preview-grid">
                                                            <div className="theme-preview-grid-item">
                                                                <span className="theme-grid-label">Current Theme :</span>
                                                                <span className="theme-grid-value" title={theme.name}>{theme.name}</span>
                                                            </div>
                                                            <div className="theme-preview-grid-item">
                                                                <span className="theme-grid-label">Logo :</span>
                                                                {theme.clientLogoUrl ? (
                                                                    <img src={theme.clientLogoUrl} alt="Logo" className="theme-grid-logo" />
                                                                ) : (
                                                                    <span className="theme-grid-value text-muted">None</span>
                                                                )}
                                                            </div>
                                                            <div className="theme-preview-grid-item">
                                                                <span className="theme-grid-label">Brand Color :</span>
                                                                <div className="theme-grid-color-wrapper">
                                                                    <div className="theme-grid-color-swatch" style={{ background: theme.primaryColor }}></div>
                                                                </div>
                                                            </div>
                                                            <div className="theme-preview-grid-item">
                                                                <span className="theme-grid-label">Accent Color :</span>
                                                                <div className="theme-grid-color-wrapper">
                                                                    <div className="theme-grid-color-swatch" style={{ background: theme.secondaryColor }}></div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )
                                                })()
                                            )}
                                        </>
                                    )}
                                </div>
                            </div>

                            <div className="ppt-status-card">
                                <div className="status-indicator">
                                    {isPptGenerated ? (
                                        <div className="status-tag generated">
                                            <CheckCircle size={14} />
                                            <span>Already Generated</span>
                                        </div>
                                    ) : (
                                        <div className="status-tag not-generated">
                                            <AlertTriangle size={14} />
                                            <span>Not Generated Yet</span>
                                        </div>
                                    )}
                                </div>

                                <div className="ppt-actions-grid">
                                    {isPptGenerated ? (
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', width: '100%' }}>
                                            <div style={{ display: 'flex', gap: '0.75rem', width: '100%' }}>
                                                <button
                                                    className="ppt-action-btn view-btn"
                                                    onClick={handleViewPPT}
                                                    disabled={isAnyActionRunning}
                                                    style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', background: '#F1F5F9', color: '#1E293B', border: '1px solid #E2E8F0', padding: '0.75rem', borderRadius: '6px', fontWeight: '600', cursor: isAnyActionRunning ? 'not-allowed' : 'pointer', opacity: isAnyActionRunning ? 0.7 : 1 }}
                                                >
                                                    <Presentation size={18} />
                                                    <span>Edit Presentation</span>
                                                </button>
                                                <button
                                                    className="ppt-action-btn download-btn"
                                                    onClick={handleDirectDownloadPPT}
                                                    disabled={isAnyActionRunning}
                                                    style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', background: '#FF5526', color: 'white', border: 'none', padding: '0.75rem', borderRadius: '6px', fontWeight: '600', cursor: isAnyActionRunning ? 'not-allowed' : 'pointer', opacity: isAnyActionRunning ? 0.7 : 1 }}
                                                >
                                                    {isDownloading ? <CustomLoader size={18} color="#FFFFFF" /> : <Download size={18} />}
                                                    <span>Download PPTX</span>
                                                </button>
                                            </div>
                                            <button
                                                className="ppt-action-btn regenerate"
                                                onClick={() => handleGenerate(true)}
                                                disabled={isAnyActionRunning}
                                                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', background: 'transparent', color: '#64748B', border: '1px dashed #CBD5E1', padding: '0.75rem', borderRadius: '6px', fontWeight: '600', cursor: isAnyActionRunning ? 'not-allowed' : 'pointer', opacity: isAnyActionRunning ? 0.7 : 1 }}
                                            >
                                                {isRegenerating ? <CustomLoader size={16} color="#64748B" /> : <RefreshCcw size={16} />}
                                                <span>Regenerate Presentation</span>
                                            </button>
                                        </div>
                                    ) : (
                                        <button
                                            className="ppt-action-btn generate-full"
                                            onClick={() => handleGenerate(false)}
                                            disabled={isAnyActionRunning}
                                        >
                                            {isRegenerating ? <CustomLoader size={20} color="#FFFFFF" /> : <Sparkles size={20} />}
                                            <span>Generate Presentation</span>
                                        </button>
                                    )}
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default PPTGenerationModal;
